import { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Title,
  Text,
  Group,
  Button,
  Tabs,
  Checkbox,
  Stack,
  LoadingOverlay,
  Box,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons-react";
import {
  MENU_RESOURCES,
  CONFIGURABLE_ROLES,
  MenuResource,
} from "constants/resources";
import {
  fetchRolePermissions,
  updateRolePermissions,
} from "services/rolePermissions";
import { Role } from "entities/user/role";
import _ from "lodash";

export const RolePermissionsList = () => {
  const theme = useMantineTheme();
  const [selectedRole, setSelectedRole] = useState<string>(
    CONFIGURABLE_ROLES[0],
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const keys = await fetchRolePermissions(selectedRole);
        setSelectedKeys(keys);
      } catch {
        setSelectedKeys([]);
        showNotification({
          title: "Error",
          message: "Failed to load permissions",
          color: "red",
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedRole]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRolePermissions(selectedRole, selectedKeys);
      showNotification({
        title: "Saved",
        message: `Permissions updated for ${_.startCase(selectedRole)}`,
        color: "green",
      });
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to save permissions",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleResource = (key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleSection = (sectionKey: string, children: MenuResource[]) => {
    const childKeys = children.map((c) => c.key);
    const allSelected = childKeys.every((k) => selectedKeys.includes(k));

    if (allSelected) {
      // Deselect all children + section
      setSelectedKeys((prev) =>
        prev.filter((k) => k !== sectionKey && !childKeys.includes(k)),
      );
    } else {
      // Select all children + section
      setSelectedKeys((prev) => {
        const newKeys = new Set(prev);
        newKeys.add(sectionKey);
        childKeys.forEach((k) => newKeys.add(k));
        return Array.from(newKeys);
      });
    }
  };

  const toggleAll = () => {
    const allKeys = MENU_RESOURCES.map((r) => r.key);
    const allSelected = allKeys.every((k) => selectedKeys.includes(k));
    setSelectedKeys(allSelected ? [] : allKeys);
  };

  // Group resources: top-level items + sections with children
  const groupedResources = useMemo(() => {
    const sections: {
      section: MenuResource;
      children: MenuResource[];
    }[] = [];
    const topLevel: MenuResource[] = [];

    const sectionMap = new Map<string, MenuResource[]>();

    MENU_RESOURCES.forEach((r) => {
      if (r.isSection) {
        sectionMap.set(r.key, []);
      }
    });

    MENU_RESOURCES.forEach((r) => {
      if (r.isSection) return;
      if (r.parent && sectionMap.has(r.parent)) {
        sectionMap.get(r.parent)!.push(r);
      } else {
        topLevel.push(r);
      }
    });

    MENU_RESOURCES.forEach((r) => {
      if (r.isSection) {
        sections.push({ section: r, children: sectionMap.get(r.key) || [] });
      }
    });

    return { topLevel, sections };
  }, []);

  const allKeys = MENU_RESOURCES.map((r) => r.key);
  const allSelected = allKeys.every((k) => selectedKeys.includes(k));
  const someSelected =
    allKeys.some((k) => selectedKeys.includes(k)) && !allSelected;

  return (
    <Paper p="md">
      <Group position="apart" mb="lg">
        <Title order={3}>Menu Permissions</Title>
        <Button
          leftIcon={<IconDeviceFloppy size={16} />}
          onClick={handleSave}
          loading={saving}
        >
          Save
        </Button>
      </Group>

      <Text size="sm" color="dimmed" mb="md">
        Configure which menu items each role can access. Admin, Developer, and
        QA roles always have full access.
      </Text>

      <Tabs value={selectedRole} onTabChange={(v) => setSelectedRole(v!)}>
        <Tabs.List mb="lg">
          {CONFIGURABLE_ROLES.map((role) => (
            <Tabs.Tab key={role} value={role}>
              {_.startCase(role)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Box style={{ position: "relative", minHeight: 200 }}>
        <LoadingOverlay visible={loading} />

        <Paper p="md" withBorder>
          <Checkbox
            label="Select All"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={toggleAll}
            mb="md"
            styles={{ label: { fontWeight: 700 } }}
          />

          <Divider mb="md" />

          <Stack spacing="lg">
            {/* Top-level resources */}
            {groupedResources.topLevel.map((r) => (
              <Checkbox
                key={r.key}
                label={r.label}
                checked={selectedKeys.includes(r.key)}
                onChange={() => toggleResource(r.key)}
              />
            ))}

            {/* Sections with children */}
            {groupedResources.sections.map(({ section, children }) => {
              const childKeys = children.map((c) => c.key);
              const allChildrenSelected = childKeys.every((k) =>
                selectedKeys.includes(k),
              );
              const someChildrenSelected =
                childKeys.some((k) => selectedKeys.includes(k)) &&
                !allChildrenSelected;

              return (
                <Box key={section.key}>
                  <Checkbox
                    label={section.label}
                    checked={allChildrenSelected}
                    indeterminate={someChildrenSelected}
                    onChange={() => toggleSection(section.key, children)}
                    styles={{
                      label: {
                        fontWeight: 600,
                        color:
                          theme.colorScheme === "dark"
                            ? theme.colors.gray[2]
                            : theme.colors.dark[6],
                      },
                    }}
                  />
                  <Stack spacing="xs" ml="xl" mt="xs">
                    {children.map((child) => (
                      <Checkbox
                        key={child.key}
                        label={child.label}
                        checked={selectedKeys.includes(child.key)}
                        onChange={() => toggleResource(child.key)}
                      />
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Box>
    </Paper>
  );
};
